/* eslint-disable @typescript-eslint/no-explicit-any */
import { Component, inject } from '@angular/core';
import { Title } from '@angular/platform-browser';
import { Router } from '@angular/router';
import { Store } from '@app/config/store';
import { AuthService } from '@app/services/auth.service';
import { CommonService } from '@app/services/common.service';
import { Gstr1Service } from '@app/services/gstr1.service';
import { MgmtService } from '@app/services/mgmt.service';
import { Gstin_Resp } from '@app/shared/header/header.component';
import { Business_Context, Company, Entity, Gstin } from '@app/types/global.type';
import { GSTR3BStatus } from '@app/types/gstr3b.type';
import { getGstinList } from '@app/utils/gstin';
import { environment } from '@env/environment';
import { lastValueFrom } from 'rxjs';

@Component({
  selector: 'app-redirect',
  standalone: true,
  imports: [],
  templateUrl: './redirect.component.html',
  styleUrl: './redirect.component.css',
})
export class RedirectComponent {
  token: string = '';
  redirectURL: string = '';
  iframe: boolean = false;
  sidebarHide: boolean = false;
  gstinNo: string = '';
  filingPeriod: string = '';
  rootCompanyId: string = '';
  sapphireTenent: string = 'null';
  selectedPan: string = '';
  titleService = inject(Title);
  appTitle = environment.TITLE;
  favIconUrl = environment.FAVICON_URL;

  constructor(
    private router: Router,
    private store: Store,
    private authSvc: AuthService,
    private mgmtSvc: MgmtService,
    private commonSvc: CommonService,
    private gstr1Svc: Gstr1Service,
  ) {
    this.token = this.router.parseUrl(this.router.url).queryParams['token'];
    this.redirectURL = this.router.parseUrl(this.router.url).queryParams['redirectUrl'];
    this.iframe = this.router.parseUrl(this.router.url).queryParams['iframe'];
    this.sidebarHide = this.router.parseUrl(this.router.url).queryParams['sidebarHide'];
    this.gstinNo = this.router.parseUrl(this.router.url).queryParams['gstin'];
    this.filingPeriod = this.router.parseUrl(this.router.url).queryParams['fp'];
    this.selectedPan = this.router.parseUrl(this.router.url).queryParams['pan'];
    if (this.selectedPan) {
      this.gstr1Svc.setSelectedPan(this.selectedPan);
      this.store.setPan(this.selectedPan);
    }
    if (this.gstinNo) {
      this.gstr1Svc.setSelectedGstin(this.gstinNo);
      this.store.setGstin(this.gstinNo);
    } else {
      localStorage.removeItem('selectedGstin');
      this.store.setGstin('');
    }
    localStorage.setItem('filingPeriod', this.filingPeriod);
    this.store.setFilingPeriod(this.filingPeriod);
    this.rootCompanyId = this.router.parseUrl(this.router.url).queryParams[
      'rootCompanyId'
    ];
    this.sapphireTenent =
      this.router.parseUrl(this.router.url).queryParams['sapphireTenent'] ?? 'asp';
    this.redirect();
    this.titleService.setTitle(this.appTitle);
    this.setFavicon(this.favIconUrl);
  }

  setFavicon(faviconUrl: string) {
    let link: HTMLLinkElement | null = document.querySelector("link[rel~='icon']");
    if (!link) {
      link = document.createElement('link');
      link.rel = 'icon';
      document.head.appendChild(link);
    }
    link.href = faviconUrl;
  }

  async redirect() {
    if (this.token) {
      localStorage.clear();
      this.store.setTenant(this.sapphireTenent);
      this.store.setIframe(Boolean(this.iframe));
      this.store.authToken.set(null);
      this.authSvc.validateToken(this.token).subscribe(async (resp) => {
        if (resp) {
          this.store.setUser(resp);
          const businessObj: Business_Context = {
            companyid: String(resp?.companyid),
            companyName: '',
            rootCompanyName: '',
            rootCompanyId: this.rootCompanyId ? this.rootCompanyId : '',
            gstinNo: this.gstinNo ? this.gstinNo : '',
            registrationDate: '',
          };
          this.store.setBusiness(businessObj);
          this.fetchBusinessHierarchy(businessObj.rootCompanyId.toString());
          const entityResp: Entity[] = await lastValueFrom(this.mgmtSvc.getEntityList());
          this.store.setBusinessEntityList(entityResp ?? []);
          this.store.sideNavHide.set(Boolean(this.sidebarHide));
          const gstinListResp = await lastValueFrom(
            this.mgmtSvc.getGstinList(this.store.user()?.email, this.rootCompanyId),
          );
          await this.fetchGstr3bStatus();
          const gstinListData = this.manipulateData(gstinListResp);
          const businessContext = this.store.businessContext() as Business_Context;
          this.store.setGstinList(getGstinList(businessContext, gstinListData));
          if (this.commonSvc.gstrStatus()?.status === 'FILED') {
            this.router.navigate(['/sapphire/gstr3b/create']);
          } else {
            this.router.navigate([this.redirectURL]);
          }
        }
      });
    }
  }

  fetchBusinessHierarchy(rootCompanyId: string) {
    this.mgmtSvc.getRegistrations(rootCompanyId);
    this.mgmtSvc.getBusinessHierarchy({ companyid: rootCompanyId }).subscribe({
      next: (resp: any) => {
        const company: Company = resp.response ?? null;
        this.store.setCompany(company);
      },
    });
  }

  fetchGstr3bStatus() {
    if (!this.gstinNo || !this.filingPeriod) return;
    this.commonSvc
      .getReturnStatus({
        filingperiod: this.filingPeriod,
        gstin: this.gstinNo,
        returntype: 'GSTR3B',
      })
      .subscribe({
        next: (resp: GSTR3BStatus) => {
          this.commonSvc.gstr3BStatus.set(resp);
          this.commonSvc.updateGstrStatus(resp.status);
        },
      });
  }

  manipulateData(list: Gstin_Resp[]) {
    const tempList: Gstin[] = [];
    list.forEach((item: Gstin_Resp) => {
      tempList.push({
        rootCompanyName: '',
        companyId: item.companyId,
        label: item.companyName,
        value: item.gstnNo,
        rootCompanyId: item.rootCompanyId,
        checked: false,
        taxpayerType: item.taxpayerType ?? '',
      });
    });
    return tempList;
  }
}
